import { useRef, useState } from 'react';
import { Card, CardContent } from '../ui';
import { api, type RadarRow, type RadarDeveloperContact, type RadarDeveloperResearch } from '../../lib/api';
import { BAND_TONE } from './bands';
import {
  DEVELOPMENT_BAND_LABEL, asSentence, isHandedToResearch, justFiled, localDayOf, sizeLine, stageLine, usd,
  type DevelopmentOutcome,
} from './developmentLane';

/**
 * One project on the new-development lane (spec 9.2) - its own component because DealRadar.tsx was already
 * 1,148 lines.
 *
 * ⛔⛔ NO CALL BUTTON, NO `tel:` LINK, NO CALL OUTCOME. A CEQA filing names a developer, never a person to ring;
 *    nobody is callable until a PERSON captures them (spec 9.6), and the API itself refuses a call outcome on
 *    this lane (`outcomesFor`). Two dispositions exist, and only those two are offered.
 * ⭐ Every AI field carries the filing's own words one click away - the reason anyone can trust a size or a
 *   developer the model read.
 * ⭐ The PAGE owns the dispositions (its `mark`), so Undo and the queue's local removal keep working; this row
 *   owns research and capture, which touch nothing else until a contact is saved.
 */

type FormKey = 'name' | 'title' | 'company' | 'phone' | 'email' | 'source';

/** Each capture field and its VISIBLE label - a placeholder alone vanishes the moment he types. */
const FIELDS: Array<[FormKey, string]> = [
  ['name', 'Name (required)'], ['title', 'Title'], ['company', 'Company'],
  ['phone', 'Phone'], ['email', 'Email'], ['source', 'Where you found them (URL)'],
];

const EMPTY_FORM: Record<FormKey, string> = { name: '', title: '', company: '', phone: '', email: '', source: '' };

/** What a research answer offers the capture form - nothing for an honest nothing or an answer from memory. */
const offered = (result: RadarDeveloperResearch, filedCompany: string): Partial<Record<FormKey, string>> | null => {
  if (result.status === 'proposed' && result.proposal) {
    return {
      name: result.proposal.name,
      title: result.proposal.title ?? '',
      company: result.company ?? filedCompany,
      phone: result.proposal.phone ?? '',
      email: result.proposal.email ?? '',
      source: result.proposal.sourceUrl,
    };
  }
  // A company and the page it was found on, but nobody named: the form opens for a researcher.
  if (result.status === 'company_only') return { company: result.company ?? filedCompany, source: result.companySource ?? '' };
  return null;
};

/**
 * What one lookup did, in ONE line - never a fill that did not happen (N1), and each reason said ONCE. A proposal's
 * cost sits in its citation; every other answer carries it here.
 */
const outcomeLine = (r: RadarDeveloperResearch, held: boolean): string => {
  if (r.status === 'proposed' && r.proposal) {
    const who = `${r.proposal.name}${r.company ? ` at ${r.company}` : ''}`;
    return held ? `Found ${who} - press Use this instead to replace your entry.` : `Found ${who} - filled in below.`;
  }
  const cost = ` This lookup cost ${usd(r.costUsd)}.`;
  if (r.status === 'company_only') {
    // "Found X but nobody named" IS its reason ("A source was found but nobody was named"), so it is not repeated.
    const what = r.companySource ? 'the company and the page it was found on' : 'the company';
    const landed = held
      ? `press Use this instead to replace your entry with ${what}`
      : `${what} ${r.companySource ? 'are' : 'is'} filled in below`;
    return `Found ${r.company ?? 'the company'} but nobody named. Over to a researcher - ${landed}.${cost}`;
  }
  if (r.status === 'rejected_no_search') return `Discarded: no web search ran, so that answer came from memory rather than a source.${cost}`;
  // ⛔ "nothing" is two different facts - a search that found nobody, or an answer caught with no citation - so its
  //   reason IS the line, said once; the generic words only when the server gave none.
  return `${asSentence(r.reason?.trim() || 'No developer contact found with a source')} Over to a researcher.${cost}`;
};

/**
 * ⛔ The server refuses a contact name over 200 characters with a 400 (`development_contacts.name` is
 *   `char_length(name) between 1 and 200`), so the field stops there instead of letting him type a name the
 *   save will refuse. The browser counts UTF-16 units, never more than the characters the server counts.
 */
const CONTACT_NAME_MAX_CHARS = 200;

/**
 * Where a proposal was found, and what the lookup cost. ⛔ `ownDomain` is a FACT about the URL - the developer's own
 * site or not. Never a score.
 */
function Citation({ answer, testId }: { answer: RadarDeveloperResearch; testId: string }) {
  if (!answer.proposal) return null;
  return (
    <div data-testid={testId} className="mt-1 flex flex-wrap items-center gap-2 text-[12.5px]">
      <span className={answer.proposal.ownDomain ? 'text-emerald-800' : 'text-amber-800'}>
        {answer.proposal.ownDomain ? "Found on the developer's own site" : 'Found on a third-party page - read it before saving'}
      </span>
      <a href={answer.proposal.sourceUrl} target="_blank" rel="noopener noreferrer" className="text-blue-700 hover:underline">
        {answer.proposal.sourceUrl}
      </a>
      <span className="text-gray-500">{`This lookup cost ${usd(answer.costUsd)}.`}</span>
    </div>
  );
}

/** The filing's exact words behind one AI field, one click away (spec 9.2). */
function Source({ label, quote, portalUrl, received }: {
  label: string; quote: string; portalUrl: string; received?: string;
}) {
  return (
    <details data-testid="dev-source" className="text-[12px] text-gray-600">
      <summary className="cursor-pointer text-blue-700">{label}</summary>
      <blockquote className="mt-1 border-l-2 border-gray-200 pl-2 italic text-gray-700">"{quote}"</blockquote>
      <a href={portalUrl} target="_blank" rel="noopener noreferrer" className="text-blue-700 hover:underline">
        {received ? `CEQA filing received ${received}` : 'CEQA filing'}
      </a>
    </details>
  );
}

export interface DevelopmentLaneRowProps {
  row: RadarRow;
  /** The page's disposition for this row is in flight. */
  busy: boolean;
  onMark: (row: RadarRow, outcome: DevelopmentOutcome) => void;
  /** A person saved a developer contact; the server cleared a research hand-off - never a "not a fit". */
  onCaptured: (entityId: string, contact: RadarDeveloperContact) => void;
  /** His calendar date for the "Just filed" chip. Injectable so a test is not tied to the day it runs. */
  today?: Date;
}

export default function DevelopmentLaneRow({ row, busy, onMark, onCaptured, today = new Date() }: DevelopmentLaneRowProps) {
  const [researching, setResearching] = useState(false);
  const [saving, setSaving] = useState(false);
  /** The latest answer research gave. */
  const [research, setResearch] = useState<RadarDeveloperResearch | null>(null);
  /** The answer the form's values came from, WHOLE - its citation stays beside them whatever a later lookup says. */
  const [filledFrom, setFilledFrom] = useState<RadarDeveloperResearch | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState<Record<FormKey, string>>(EMPTY_FORM);
  /**
   * ⛔ RULING (N1): the fields HE edited. A research fill never counts; his edit of a research-filled field does. A ref,
   *   so a lookup's answer reads it as it stands when the answer lands - he may type while the lookup runs.
   */
  const typed = useRef<Set<FormKey>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const handed = isHandedToResearch(row);
  const captured = row.capturedContact ?? null;
  const band = (row.band ?? 'early') as keyof typeof DEVELOPMENT_BAND_LABEL;
  const filedCompany = row.developer?.company ?? row.developer?.name ?? '';
  const offer = research ? offered(research, filedCompany) : null;
  /** ⛔ RULING (N1): a proposal he has not taken - his entry stands, and it waits beside it for "Use this instead". */
  const held = offer !== null && filledFrom !== research;

  const openForm = () => {
    // ⛔ Already open: what a person typed stays, and so does the citation beside it.
    if (formOpen) return;
    typed.current = new Set();
    setForm({ ...EMPTY_FORM, company: filedCompany });
    setFormOpen(true);
    setResearch(null);
    setFilledFrom(null);
    setError(null);
  };

  /** Cancel DISCARDS - the form, and the lookup's line with it: that line would claim a fill the form no longer holds. */
  const closeForm = () => {
    typed.current = new Set();
    setFormOpen(false);
    setForm(EMPTY_FORM);
    setResearch(null);
    setFilledFrom(null);
  };

  /** ⛔ RULING (N1): a proposal taken WHOLE - every field from it, none of his, none of an earlier lookup's. */
  const takeWhole = (answer: RadarDeveloperResearch, found: Partial<Record<FormKey, string>>) => {
    typed.current = new Set();
    setForm({ ...EMPTY_FORM, ...found });
    setFilledFrom(answer);
    setFormOpen(true);
  };

  /** "Use this instead": the held proposal, taken whole - his typed values go only on this press. */
  const takeProposal = () => {
    if (research && offer) takeWhole(research, offer);
  };

  /**
   * ⛔ METERED: one press, one lookup, checked against the 30-day cap BEFORE anything is spent (a 409 names the
   *   cap). It PROPOSES into the form below and never saves.
   */
  const runResearch = async () => {
    setResearching(true);
    setError(null);
    setNotice(null);
    try {
      const { result } = await api.researchRadarDeveloper(row.entity_id);
      setResearch(result);
      const found = offered(result, filedCompany);
      /**
       * ⛔⛔ RULING (N1): WHOLE OR NOT AT ALL - never one contact built out of two people. With no field his, the answer
       *    fills the whole form (an earlier lookup's fill included); with any field his, it changes NONE and waits
       *    beside his entry. A nothing, or an answer from memory, leaves the form exactly as it was, open if it was.
       */
      if (found && typed.current.size === 0) takeWhole(result, found);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not research that developer');
    } finally {
      setResearching(false);
    }
  };

  const save = async () => {
    // ⛔ A NAME IS THE MINIMUM - the API refuses without one too (plan Task 18).
    if (!form.name.trim()) {
      setError('A name is required - a number with nobody behind it is not a contact');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const res = await api.saveRadarDeveloperContact({
        entity_id: row.entity_id,
        name: form.name.trim(),
        title: form.title.trim() || undefined,
        company: form.company.trim() || undefined,
        phone: form.phone.trim() || undefined,
        email: form.email.trim() || undefined,
        source_url: form.source.trim() || undefined,
      });
      typed.current = new Set();
      setFormOpen(false);
      setResearch(null);
      setFilledFrom(null);
      setForm(EMPTY_FORM);
      onCaptured(row.entity_id, {
        name: res.contact.name, title: res.contact.title, company: res.contact.company, phone: res.contact.phone,
        email: res.contact.email, sourceUrl: res.contact.sourceUrl, capturedAt: new Date().toISOString(),
        ghlContactId: res.ghlContactId,
      });
      // ⛔ A GHL failure does not lose the contact - it is saved here. Say so.
      if (res.ghlError) setNotice(`Saved here, but GHL refused it (${res.ghlError})`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save that contact');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card data-testid="development-row">
      <CardContent className="py-3.5">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <div data-testid="dev-name" className="text-[15px] font-medium text-gray-900">
              {row.name ?? 'Unnamed project'}{row.city ? `, ${row.city}` : ''}
            </div>
            <div className="mt-0.5 text-[13px] text-gray-600">
              {row.latest_portal_url ? (
                <a href={row.latest_portal_url} target="_blank" rel="noopener noreferrer" className="hover:underline">
                  {stageLine(row)}
                </a>
              ) : stageLine(row)}
            </div>
            <div className="mt-0.5 text-[13px] text-gray-600">
              {[row.use_label, row.kind_label].filter(Boolean).join(' · ') || 'use not stated'}
              {' · '}
              <span data-testid="dev-size">{sizeLine(row)}</span>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            {justFiled(row, today) && (
              <span data-testid="dev-just-filed"
                className="whitespace-nowrap rounded border border-violet-200 bg-violet-50 px-2 py-0.5 text-[12px] text-violet-800">
                Just filed
              </span>
            )}
            <span data-testid="dev-band"
              className={`whitespace-nowrap rounded border px-2 py-0.5 text-[12px] ${BAND_TONE[band] ?? ''}`}>
              {DEVELOPMENT_BAND_LABEL[band] || row.band}
            </span>
          </div>
        </div>

        {row.returnReason && (
          <div data-testid="dev-return-reason"
            className="mt-2 inline-block rounded border border-amber-200 bg-amber-50 px-2 py-1 text-[12.5px] text-amber-800">
            Back on the list - {row.returnReason}
          </div>
        )}

        <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1">
          {row.use_source && (
            <Source label={`Source: ${row.use_label ?? 'use'}`} quote={row.use_source.quote}
              portalUrl={row.use_source.portal_url} received={row.use_source.received} />
          )}
          {row.kind_source && (
            <Source label={`Source: ${row.kind_label ?? 'kind'}`} quote={row.kind_source.quote}
              portalUrl={row.kind_source.portal_url} received={row.kind_source.received} />
          )}
          {row.building_sf_source && (
            <Source label="Source: building size" quote={row.building_sf_source.quote}
              portalUrl={row.building_sf_source.portal_url} received={row.building_sf_source.received} />
          )}
          {row.site_acres_source && (
            <Source label="Source: site size" quote={row.site_acres_source.quote}
              portalUrl={row.site_acres_source.portal_url} received={row.site_acres_source.received} />
          )}
        </div>

        <div className="mt-2 border-t border-gray-100 pt-2 text-[13px] text-gray-600">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="rounded bg-blue-50 px-1.5 py-0.5 text-[11.5px] uppercase tracking-wide text-blue-800">
              Developer
            </span>
            {row.developer ? (<>
              <span data-testid="dev-developer" className="text-gray-900">{row.developer.company ?? row.developer.name}</span>
              {row.developer.company && row.developer.name !== row.developer.company && (
                <span className="text-gray-500">{row.developer.name}</span>
              )}
              <Source label="source" quote={row.developer.quote} portalUrl={row.developer.portal_url} />
            </>) : (
              <span data-testid="dev-developer-missing" className="text-gray-500">developer not named</span>
            )}
          </div>
          {(row.consultants ?? []).length > 0 && (
            <div data-testid="dev-consultants" className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-[12px] text-gray-500">
              {/* ⛔ Context, never a lead - and like every AI field, the filing's own words one click away (spec 9.2). */}
              {(row.consultants ?? []).map((c, i) => (
                <div key={`${c.name}-${i}`} className="flex flex-wrap items-center gap-x-1.5">
                  <span>
                    Consultant: {c.company ?? c.name} <span className="text-gray-400">(not a lead)</span>
                  </span>
                  <Source label="source" quote={c.quote} portalUrl={c.portal_url} />
                </div>
              ))}
            </div>
          )}
          <div className="mt-1 flex flex-wrap gap-x-2 text-[12.5px]">
            <span>Lead agency: {row.lead_agency ?? 'not stated'}</span>
            {(row.case_numbers ?? []).length > 0 && <span>· Case {(row.case_numbers ?? []).join(', ')}</span>}
            {(row.sch_numbers ?? []).length > 0 && <span>· SCH {(row.sch_numbers ?? []).join(', ')}</span>}
            {(row.apns ?? []).length > 0 && <span>· APN {(row.apns ?? []).join(', ')}</span>}
            {row.county && <span>· {row.county} County</span>}
          </div>
          {(row.on_property ?? []).length > 0 && (
            <div data-testid="dev-on-property" className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
              <span className="rounded bg-emerald-50 px-1.5 py-0.5 text-[11.5px] uppercase tracking-wide text-emerald-800">
                On your property
              </span>
              {/* ⛔ `/data-hub/:id` - a `master_properties` id, never the analyzer's `/properties/:id`. */}
              {(row.on_property ?? []).map((p) => (
                <a key={p.master_property_id} href={`/data-hub/${p.master_property_id}`} className="text-blue-700 hover:underline">
                  {p.address ?? 'Open the property'}
                </a>
              ))}
            </div>
          )}
          {(row.filings ?? []).length > 0 && (
            <div data-testid="dev-filings" className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12.5px]">
              <span className="text-gray-500">CEQA filings:</span>
              {(row.filings ?? []).map((f) => (
                <a key={f.portal_url} href={f.portal_url} target="_blank" rel="noopener noreferrer"
                  className="text-blue-700 hover:underline">
                  {f.label} {f.received}
                </a>
              ))}
            </div>
          )}
          {/*
            ⭐⭐ THE ONLY PLACE A DEVELOPER NUMBER APPEARS, because a PERSON saved it. The source stays on the row:
               it separates a contact somebody found from one a model invented.
          */}
          {captured && (
            <div data-testid="dev-captured"
              className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 rounded border border-emerald-200 bg-emerald-50 px-2 py-1">
              <span className="text-[11.5px] uppercase tracking-wide text-emerald-800">Developer contact</span>
              <span className="text-gray-900">{captured.name}</span>
              {captured.title && <span className="text-gray-600">{captured.title}</span>}
              {captured.company && <span className="text-gray-600">{captured.company}</span>}
              {/* ⛔ Selectable TEXT, never a tel: link - on iOS a tel: call is never recorded in GHL. */}
              {captured.phone && <span className="font-mono">{captured.phone}</span>}
              {captured.email && <span className="text-gray-600">{captured.email}</span>}
              {captured.sourceUrl && (
                <a href={captured.sourceUrl} target="_blank" rel="noopener noreferrer" className="text-blue-700 hover:underline">
                  source
                </a>
              )}
              <span className="text-[12px] text-gray-500">
                saved {localDayOf(captured.capturedAt)} · {captured.ghlContactId ? 'in GHL' : 'not in GHL'}
              </span>
            </div>
          )}
        </div>

        <div className="mt-2.5 flex flex-wrap items-center gap-1.5 border-t border-gray-100 pt-2.5">
          {/* ⛔ Once a contact is saved there is nothing left to research or add. */}
          {!captured && (<>
            <button type="button" data-testid="dev-research" onClick={() => void runResearch()} disabled={researching}
              className="rounded border border-violet-300 bg-violet-50 px-2.5 py-1.5 text-[13px] text-violet-900 hover:bg-violet-100 disabled:opacity-50">
              {researching ? 'Searching...' : 'Research the developer'}
            </button>
            <button type="button" data-testid="dev-add-contact" onClick={openForm}
              className="rounded border border-gray-300 px-2.5 py-1.5 text-[13px] text-gray-700 hover:bg-gray-50">
              Add contact
            </button>
            {handed ? (
              <span data-testid="dev-handed" className="py-1.5 pl-1 text-[13px] text-gray-500">Handed to research</span>
            ) : (
              <button type="button" data-testid="dev-mark-developer_research"
                onClick={() => onMark(row, 'developer_research')} disabled={busy}
                className="rounded border border-gray-300 px-2.5 py-1.5 text-[13px] text-gray-700 hover:bg-gray-50 disabled:opacity-50">
                Hand to research
              </button>
            )}
          </>)}
          <button type="button" data-testid="dev-mark-not_a_fit" onClick={() => onMark(row, 'not_a_fit')} disabled={busy}
            className="rounded border border-gray-300 px-2.5 py-1.5 text-[13px] text-gray-700 hover:bg-gray-50 disabled:opacity-50">
            Not a fit
          </button>
        </div>

        {/* ⛔⛔ AN HONEST NOTHING IS A RESULT - and an answer from memory is DISCARDED, however it reads. */}
        {research && (
          <div data-testid="dev-research-outcome"
            className="mt-2 rounded border border-violet-200 bg-violet-50 px-3 py-2 text-[12.5px] text-violet-900">
            {outcomeLine(research, held)}
          </div>
        )}

        {formOpen && (
          <div data-testid="dev-contact-form" className="mt-2 rounded border border-gray-200 bg-gray-50 px-3 py-2.5">
            <div className="text-[12.5px] text-gray-600">
              Developer contact for <span className="font-medium text-gray-900">{row.name ?? 'this project'}</span>
            </div>
            {/*
              ⭐⭐⭐ THE CITATION SITS BESIDE THE FIELDS SOMEBODY IS ABOUT TO ACCEPT - the one the fields came from, whole.
            */}
            {filledFrom?.status === 'proposed' && <Citation answer={filledFrom} testId="dev-proposal-citation" />}
            {/* ⛔ RULING (N1): HIS entry stands. What research found waits here, cited, taken only whole and only on his press. */}
            {held && research && offer && (
              <div data-testid="dev-held-proposal" className="mt-1.5 rounded border border-violet-200 bg-white px-2.5 py-2 text-[12.5px]">
                <div className="text-gray-700">
                  Research found: {[offer.name, offer.title, offer.company, offer.phone, offer.email].filter(Boolean).join(' · ')}
                </div>
                {research.status === 'proposed' ? <Citation answer={research} testId="dev-held-citation" /> : offer.source && (
                  <a href={offer.source} target="_blank" rel="noopener noreferrer" className="mt-1 inline-block text-blue-700 hover:underline">
                    {offer.source}
                  </a>
                )}
                <div className="mt-1.5 flex flex-wrap items-center gap-2">
                  <button type="button" data-testid="dev-use-proposal" onClick={takeProposal}
                    className="rounded border border-violet-300 bg-violet-50 px-2.5 py-1 text-[13px] text-violet-900 hover:bg-violet-100">
                    Use this instead
                  </button>
                  <span className="text-[12px] text-gray-500">It replaces everything in the form below.</span>
                </div>
              </div>
            )}
            <div className="mt-2 grid gap-2 sm:grid-cols-2">
              {FIELDS.map(([key, label]) => (
                <label key={key}
                  className={`flex flex-col gap-0.5 text-[12px] text-gray-600${key === 'source' ? ' sm:col-span-2' : ''}`}>
                  {label}
                  <input data-testid={`dev-contact-${key}`} value={form[key]}
                    maxLength={key === 'name' ? CONTACT_NAME_MAX_CHARS : undefined}
                    onChange={(e) => {
                      // ⛔ RULING (N1): HIS edit - of an empty field or a research-filled one - makes the field his.
                      typed.current.add(key);
                      const { value } = e.target;
                      setForm((f) => ({ ...f, [key]: value }));
                    }}
                    className="rounded border border-gray-300 px-2 py-1 text-[13px] text-gray-900" />
                </label>
              ))}
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              <button type="button" data-testid="dev-save-contact" onClick={() => void save()} disabled={saving}
                className="rounded border border-emerald-300 bg-emerald-50 px-3 py-1.5 text-[13px] font-medium text-emerald-900 hover:bg-emerald-100 disabled:opacity-50">
                Save the contact
              </button>
              <button type="button" data-testid="dev-cancel-contact" onClick={closeForm}
                className="rounded px-2 py-1.5 text-[13px] text-gray-600 hover:text-gray-900">
                Cancel
              </button>
              <span className="text-[12px] text-gray-500">Saves here, and into GHL when there is a phone or an email.</span>
            </div>
          </div>
        )}

        {error && <div data-testid="dev-error" className="mt-1.5 text-[12.5px] text-red-700">{error}</div>}
        {notice && <div data-testid="dev-notice" className="mt-1.5 text-[12.5px] text-amber-800">{notice}</div>}
      </CardContent>
    </Card>
  );
}
